import React, { useRef } from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import type { FacilitatorLiveSessionParticipant } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { ControlTowerParticipantRow } from './ControlTowerParticipantRow'
import { ControlTowerRosterColumns } from './ControlTowerRosterColumns'
import {
  buildControlTowerRosterHeaderSx,
  buildControlTowerRosterRowsSx,
  controlTowerRosterBodyCellSx,
  controlTowerRosterFrameSx,
  controlTowerRosterHeaderCellSx,
  controlTowerRosterTableSx
} from './controlTowerStyles'
import { useVisibleRowsMaxHeight } from './useVisibleRowsMaxHeight'

interface ControlTowerParticipantTableProps {
  participants: FacilitatorLiveSessionParticipant[]
  showPlayedColumn: boolean
  testIdPrefix: string
  /** Shows this many rows and scrolls the rest under the header, for a list as long as a whole sprint. */
  visibleRowCount?: number
}

/** Who is expected, who is in the call, and who has acted in the current loop — one row per team member. */
export const ControlTowerParticipantTable: React.FC<ControlTowerParticipantTableProps> = ({
  participants,
  showPlayedColumn,
  testIdPrefix,
  visibleRowCount
}) => {
  const { t } = useTranslation()
  const rowsRef = useRef<HTMLDivElement>(null)
  const maxHeightPx = useVisibleRowsMaxHeight(rowsRef, visibleRowCount, participants)
  const isScrollable = visibleRowCount !== undefined
  const isEmpty = participants.length === 0

  // Two tables keep the header still over the scrolling rows; assistive technology reads the frame as one table.
  return (
    <Box data-testid={`${testIdPrefix}-roster`} role="table" sx={controlTowerRosterFrameSx}>
      <Box sx={buildControlTowerRosterHeaderSx(isScrollable)}>
        <Table size="small" role="presentation" sx={controlTowerRosterTableSx}>
          <ControlTowerRosterColumns showPlayedColumn={showPlayedColumn} />
          <TableHead role="rowgroup">
            <TableRow role="row">
              <TableCell role="columnheader" sx={controlTowerRosterHeaderCellSx}>
                {t('facilitator.controlTower.participantColumn', 'Participant')}
              </TableCell>
              <TableCell role="columnheader" align="center" sx={controlTowerRosterHeaderCellSx}>
                {t('facilitator.controlTower.inMeetColumn', 'In meet')}
              </TableCell>
              {showPlayedColumn && (
                <TableCell role="columnheader" align="center" sx={controlTowerRosterHeaderCellSx}>
                  {t('facilitator.controlTower.playedColumn', 'Played')}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
        </Table>
      </Box>

      <Box
        ref={rowsRef}
        data-testid={`${testIdPrefix}-roster-rows`}
        sx={buildControlTowerRosterRowsSx(isScrollable, maxHeightPx)}
      >
        <Table size="small" role="presentation" sx={controlTowerRosterTableSx}>
          <ControlTowerRosterColumns showPlayedColumn={showPlayedColumn} />
          <TableBody role="rowgroup" sx={{ '& tr:last-of-type td': { borderBottom: 0 } }}>
            {isEmpty && (
              <TableRow role="row">
                <TableCell
                  role="cell"
                  colSpan={showPlayedColumn ? 3 : 2}
                  sx={{ ...controlTowerRosterBodyCellSx, color: INTERAX_COLORS.white70 }}
                >
                  {t('facilitator.controlTower.noParticipantsAssigned', 'No participants assigned')}
                </TableCell>
              </TableRow>
            )}
            {participants.map((participant, index) => (
              <ControlTowerParticipantRow
                key={participant.userId ?? `${participant.name}-${index}`}
                participant={participant}
                showPlayedColumn={showPlayedColumn}
                testId={`${testIdPrefix}-participant-${index}`}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
